/**
 * UploadedFilesList — file rows with status chips, char counts,
 * vision badges, per-file warnings, and (in editable mode) Trash +
 * Retry affordances.
 *
 * Lifted out of Stage1.tsx as part of the Stage 0 / Stage 1 split.
 * The component owns its own polling so callers don't have to
 * coordinate two sources of truth — Stage 0 (no other consumers)
 * and Stage 1 (PageIndex badge / completion toast / figures-reload
 * token, all driven by the same docs list) both subscribe via the
 * onDocumentsChange callback.
 *
 * Mode prop:
 *   - 'editable'  → Trash + Retry buttons render; clicking either
 *                   calls the matching delete / retry endpoint.
 *   - 'readonly'  → no Trash, no Retry, no delete-confirm state.
 *                   Used in Stage 1's left panel after the upload
 *                   panel was moved to Stage 0.
 *
 * refreshKey: bumping this from the parent forces an immediate
 *             refetch outside the polling cadence — used by
 *             UploadZone's onUploaded so a freshly-uploaded file
 *             surfaces without waiting up to 5 s for the next tick.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, AlertTriangle, CheckCircle, FileText, File, Trash2, Circle,
} from 'lucide-react';

import * as api from '@/api/client';
import type { MatterDocument, JobStatus } from '@/api/client';
import { useJobStatus } from '@/hooks/useJobStatus';


export type UploadedFilesMode = 'editable' | 'readonly';


interface UploadedFilesListProps {
  matterId: string;
  mode?: UploadedFilesMode;
  /** Bump to force an out-of-band refetch (e.g. immediately after
   *  UploadZone reports a successful upload). */
  refreshKey?: number;
  /** Called after every successful list fetch with the filtered
   *  (non-archived) docs. Stage 1 uses this to drive the PageIndex
   *  badge, completion toast, and figures-reload token. Best-effort:
   *  exceptions thrown by the callback are swallowed so a buggy
   *  parent can't break the polling. */
  onDocumentsChange?: (docs: MatterDocument[]) => void;
  /** Backend prevents deletes / retries while Stage 1 is frozen.
   *  Hides the affordances even in editable mode so the drafter
   *  doesn't see an action that the server will refuse. */
  isFrozen?: boolean;
  /** Optional join target for legacy idf_documents — used to surface
   *  the per-file char count + extraction warnings on primary IDF
   *  rows (those columns only exist on the legacy table). When
   *  undefined, char count + warnings are simply hidden, which is
   *  the correct shape for supplementary / prior-art uploads. */
  legacyDocsByName?: Map<string, { extraction_warnings?: string[]; extracted_text?: string }>;
  /** (document_id → job_id) map populated by UploadZone when an
   *  async upload returns 202. When a row's document_id is keyed
   *  here AND its status is in-flight, the file row mounts a
   *  JobProgressPanel that polls /api/jobs/{job_id}/status every
   *  5s and renders the worker's per-step progress (queue position,
   *  extracting / indexing / vision steps, complete checkmark).
   *  Sync-path uploads never populate this map, so the panel never
   *  renders — existing 200-path UI is unchanged. */
  jobIdsByDocument?: Record<string, string>;
}


/**
 * Step → 4-row checklist position. Map matches the spec's table:
 *   step=extracting → step 2 active
 *   step=figures    → step 2 done, step 3 active
 *   step=indexing   → step 3 active (steps 1+2 done)
 *   step=vision     → step 4 active (steps 1-3 done)
 *   step=complete   → all done
 *   queued / null   → step 1 done (file received), 2-4 pending
 *
 * Returned tuple is (activeIndex, doneThrough) where indices are
 * 0-based against [received, extract, index, vision]. doneThrough
 * is the highest 0-based index that's marked ✅; activeIndex is
 * the index currently in flight (rendered with the spinner +
 * active label). Either may be -1 to mean "none active / nothing
 * past file-received is done".
 */
function resolveStepIndices(step: string | null | undefined): {
  done: number;     // index of the LAST done step (-1 = nothing done yet)
  active: number;   // index of the currently-active step (-1 = none active)
} {
  switch (step) {
    case 'extracting': return { done: 0, active: 1 };
    case 'figures':    return { done: 1, active: 2 };
    case 'indexing':   return { done: 1, active: 2 };
    case 'vision':     return { done: 2, active: 3 };
    case 'complete':   return { done: 3, active: -1 };
    default:           return { done: 0, active: -1 };  // queued / unknown
  }
}


const STEP_LABELS = [
  'File received',
  'Extracting text',
  'Building search index',
  'Vision analysis',
];


interface JobProgressPanelProps {
  jobId: string;
  /** Fires when poll sees status='finished' / 'failed' so the
   *  parent can refetch listMatterDocuments and let the row pick
   *  up its terminal status (indexed / failed) on the next tick.
   *  Best-effort — exceptions don't unwind the poller. */
  onSettled?: () => void;
}


/**
 * JobProgressPanel — per-row step progress for an in-flight RQ
 * upload. Mounted by UploadedFilesList only when a row's
 * document_id is keyed in the jobIdsByDocument map AND the row's
 * status reflects in-flight work.
 *
 * Polls /api/jobs/{job_id}/status every 5 s. Renders:
 *   - active step label + spinner (or queue position when waiting)
 *   - 4-row checklist with ✅ / ⏳ / ○ icons
 *
 * Auto-stops the poller when status='finished' or 'failed'.
 * Cleanup on unmount via the pollRef pattern UploadZone uses.
 */
function JobProgressPanel({ jobId, onSettled }: JobProgressPanelProps) {
  // Polling logic generalised into useJobStatus (Sub-Chunk 6 of
  // the claims-async migration). Default cadence in the hook is
  // 2s; this panel historically polled at 5s for document
  // ingestion — preserved here via pollInterval.
  const { status: job, error } = useJobStatus(jobId, {
    pollInterval: 5_000,
    onSettled: onSettled ? () => onSettled() : undefined,
  });

  if (error || !job) {
    // Silent during the very first poll — the row's existing
    // status chip already conveys "something is in flight". A
    // permanent failure to fetch (e.g. job_id not found) is
    // surfaced as a small inline error so the drafter knows
    // why the panel isn't moving.
    if (error) {
      return (
        <div className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1">
          <AlertTriangle size={10} className="text-amber-500" />
          Job status unavailable ({error})
        </div>
      );
    }
    return null;
  }

  const step = job.progress?.step || (job.status === 'queued' ? 'queued' : null);
  const { done, active } = resolveStepIndices(step);
  const queued = (job.queue_position || 0) > 0;
  const queueMsg = queued
    ? `Position ${job.queue_position} in queue · ~${(job.queue_position || 0) * 30}s estimated`
    : null;
  const activeMsg = job.progress?.message
    || (queued ? queueMsg : null)
    || (step ? `${STEP_LABELS[active] || 'Processing'}…` : 'Waiting for worker…');

  return (
    <div
      data-testid={`job-progress-${jobId}`}
      data-step={step || ''}
      className="mt-2 pl-6 space-y-1"
    >
      <div className="text-[11px] text-zinc-700 flex items-center gap-1.5">
        {active === -1 && job.status !== 'finished' ? (
          <Loader2 size={10} className="animate-spin text-[#002FA7]" />
        ) : null}
        <span>{activeMsg}</span>
      </div>
      <ul className="space-y-0.5">
        {STEP_LABELS.map((label, i) => {
          const isDone = i <= done && active !== i;
          const isActive = i === active;
          const cls = isActive
            ? 'text-[#002FA7] font-medium'
            : isDone
              ? 'text-green-700'
              : 'text-zinc-400';
          const Icon = isActive
            ? Loader2
            : isDone
              ? CheckCircle
              : Circle;
          return (
            <li
              key={i}
              data-testid={`job-step-${i}`}
              data-state={isActive ? 'active' : isDone ? 'done' : 'pending'}
              className={`text-[11px] flex items-center gap-1.5 ${cls}`}
            >
              <Icon
                size={10}
                className={isActive ? 'animate-spin' : ''}
              />
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


export default function UploadedFilesList({
  matterId,
  mode = 'editable',
  refreshKey,
  onDocumentsChange,
  isFrozen,
  legacyDocsByName,
  jobIdsByDocument,
}: UploadedFilesListProps) {
  const [docs, setDocs] = useState<MatterDocument[]>([]);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<number | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editable = mode === 'editable';

  const fetchDocuments = useCallback(async (): Promise<MatterDocument[]> => {
    if (!matterId) return [];
    try {
      const data = await api.listMatterDocuments(matterId);
      const fresh = (data.documents || []).filter((d) => d.status !== 'archived');
      setDocs(fresh);
      if (onDocumentsChange) {
        try { onDocumentsChange(fresh); } catch { /* parent-callback errors are best-effort */ }
      }
      return fresh;
    } catch {
      // Polling is advisory — a transient 5xx doesn't break the flow;
      // the next tick recovers.
      return [];
    }
  }, [matterId, onDocumentsChange]);

  // Initial + 5s polling. Cadence matches the previous inline impl
  // in Stage 1: poll only while at least one row is in flight, then
  // back off so settled matters aren't spamming the API.
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    const tick = async () => {
      const fresh = await fetchDocuments();
      if (cancelled) return;
      void fresh;
    };
    tick();
    const id = setInterval(() => {
      const inFlight = docs.filter(
        (d) => d.status !== 'indexed' && d.status !== 'failed',
      ).length;
      if (inFlight > 0) tick();
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, docs.length, docs.map(d => d.status).join(',')]);

  // Out-of-band refetch trigger — UploadZone fires onUploaded after
  // each successful file POST and the parent bumps refreshKey here.
  useEffect(() => {
    if (refreshKey === undefined) return;
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleRetry = async (docId: number) => {
    setRetryingId(docId);
    setError(null);
    try {
      await api.retryMatterDocument(matterId, docId);
      await fetchDocuments();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const handleDelete = async (docId: number) => {
    if (isFrozen) return;
    if (confirmDeleteDocId !== docId) {
      setConfirmDeleteDocId(docId);
      return;
    }
    setDeletingDocId(docId);
    setError(null);
    try {
      await api.deleteMatterDocument(matterId, docId);
      // Optimistic local removal so the row vanishes instantly;
      // the next poll tick will catch any stragglers.
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete document');
    }
    setDeletingDocId(null);
    setConfirmDeleteDocId(null);
  };

  if (docs.length === 0) return null;

  const indexedCount = docs.filter((d) => d.status === 'indexed').length;
  const failedCount = docs.filter((d) => d.status === 'failed').length;
  const inFlightCount = docs.length - indexedCount - failedCount;

  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="uploaded-files">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          Uploaded Files
        </h3>
        <div
          data-testid="uploaded-files-summary"
          className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"
        >
          <span><strong className="text-zinc-700">{docs.length}</strong> file{docs.length === 1 ? '' : 's'}</span>
          {indexedCount > 0 && (
            <span className="text-green-700">· {indexedCount} indexed</span>
          )}
          {inFlightCount > 0 && (
            <span className="text-amber-700">· {inFlightCount} processing</span>
          )}
          {failedCount > 0 && (
            <span className="text-red-700">· {failedCount} failed</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-1.5">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      <div className="space-y-2">
        {docs.map((doc) => {
          const inFlight = (
            doc.status === ('uploading' as any)   // RQ async path pre-INSERT
            || doc.status === ('uploaded' as any)
            || doc.status === ('extracting' as any)
            || doc.status === ('indexing' as any)
            || doc.status === ('processing' as any)
          );
          const failed = doc.status === 'failed';
          const indexed = doc.status === 'indexed';
          const stale = !!doc.stale && inFlight;

          const armed = confirmDeleteDocId === doc.id;
          const deleting = deletingDocId === doc.id;
          const retrying = retryingId === doc.id;

          // Per-file extras (warnings + char count) live on the
          // legacy idf_document table, joined by filename. When the
          // join misses (supplementary / prior-art rows) we just
          // skip those columns.
          const legacy = legacyDocsByName?.get(doc.doc_name);
          const warns = (legacy?.extraction_warnings || []) as string[];
          const visionUsed = warns.some((w) =>
            typeof w === 'string' && w.toLowerCase().includes('vision extraction enriched'),
          );
          const userWarns = warns.filter((w) =>
            typeof w === 'string' && !w.toLowerCase().includes('vision extraction enriched'),
          );
          const hasWarnings = userWarns.length > 0;
          const charCount = (legacy?.extracted_text || '').length;

          const isPdf = (doc.file_format || '').toLowerCase() === 'pdf';

          // Status chip styling — green / amber / red mirrors the
          // summary line so the eye reads the row + the badge as
          // one signal.
          let statusLabel: string;
          let statusClass: string;
          let StatusIcon: React.ComponentType<any>;
          if (failed) {
            statusLabel = 'Failed';
            statusClass = 'bg-red-50 text-red-800 border-red-200';
            StatusIcon = AlertTriangle;
          } else if (stale) {
            statusLabel = 'Stuck';
            statusClass = 'bg-amber-50 text-amber-900 border-amber-300';
            StatusIcon = AlertTriangle;
          } else if (inFlight) {
            statusLabel = 'Processing…';
            statusClass = 'bg-amber-50 text-amber-800 border-amber-200';
            StatusIcon = Loader2;
          } else if (indexed && hasWarnings) {
            statusLabel = 'Indexed (warnings)';
            statusClass = 'bg-amber-50 text-amber-800 border-amber-200';
            StatusIcon = AlertTriangle;
          } else if (indexed) {
            statusLabel = 'Indexed';
            statusClass = 'bg-green-50 text-green-800 border-green-200';
            StatusIcon = CheckCircle;
          } else {
            statusLabel = String(doc.status);
            statusClass = 'bg-zinc-50 text-zinc-700 border-zinc-200';
            StatusIcon = FileText;
          }

          return (
            <div
              key={doc.id}
              data-testid={`uploaded-file-${doc.id}`}
              data-status={doc.status}
              data-stale={stale ? 'true' : 'false'}
              className="border border-zinc-100 rounded-sm p-2"
            >
              <div className="flex items-center gap-2 text-sm">
                {isPdf ? (
                  <File size={16} className="text-red-500 flex-shrink-0" />
                ) : (
                  <FileText size={16} className="text-blue-500 flex-shrink-0" />
                )}
                <span className="text-zinc-700 truncate flex-1">{doc.doc_name}</span>
                {indexed && charCount > 0 && (
                  <span
                    className="text-[10px] text-zinc-400 font-mono flex-shrink-0"
                    title={`${charCount.toLocaleString()} characters extracted (~${Math.round(charCount / 4).toLocaleString()} tokens)`}
                  >
                    {charCount > 1000 ? `${Math.round(charCount / 1000)}k` : charCount} chars
                  </span>
                )}
                {visionUsed && (
                  <span
                    className="text-[10px] text-purple-700 bg-purple-50 border border-purple-200 px-1 py-0 rounded-sm flex-shrink-0"
                    title="Images in this document were processed by the LLM vision pass"
                  >
                    images processed
                  </span>
                )}
                <span
                  data-testid={`uploaded-file-status-${doc.id}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-sm border ${statusClass}`}
                  title={stale
                    ? `In ${doc.status} status since ${doc.uploaded_at}; backend flagged as stuck (>5 min)`
                    : statusLabel}
                >
                  <StatusIcon size={10} className={inFlight && !stale ? 'animate-spin' : ''} />
                  {statusLabel}
                </span>
                {editable && (failed || stale) && !isFrozen && (
                  <button
                    type="button"
                    data-testid={`uploaded-file-retry-${doc.id}`}
                    onClick={() => handleRetry(doc.id)}
                    disabled={retrying}
                    title="Re-run the indexing pipeline for this document"
                    className="ml-1 text-[10px] text-[#002FA7] hover:underline disabled:opacity-50 flex-shrink-0"
                  >
                    {retrying ? 'Retrying…' : 'Retry'}
                  </button>
                )}
                {editable && !isFrozen && doc.id > 0 && (
                  <button
                    type="button"
                    data-testid={`delete-document-${doc.id}`}
                    onClick={() => handleDelete(doc.id)}
                    onBlur={() => setConfirmDeleteDocId((cur) => (cur === doc.id ? null : cur))}
                    disabled={deleting}
                    title={armed ? 'Click again to confirm' : 'Delete this document'}
                    className={`ml-1 p-1.5 rounded-sm text-xs transition-colors flex items-center gap-1 flex-shrink-0 ${
                      armed
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'text-zinc-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                  >
                    {deleting
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Trash2 size={12} />}
                    {armed && !deleting && <span className="text-[10px] pr-1">Confirm</span>}
                  </button>
                )}
              </div>
              {hasWarnings && (
                <div className="mt-1.5 pl-6 space-y-0.5">
                  {userWarns.slice(0, 3).map((w, idx) => (
                    <div key={idx} className="text-[11px] text-amber-700 flex items-start gap-1">
                      <AlertTriangle size={10} className="flex-shrink-0 mt-0.5 text-amber-500" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Per-row step-progress panel — only when the row is
                  in-flight AND we know the job_id (async-path
                  uploads). Sync-path uploads never populate the
                  map so this section never renders for them; with
                  use_async_upload=false the entire branch is
                  inert (jobIdsByDocument stays empty). */}
              {inFlight && jobIdsByDocument?.[String(doc.id)] && (
                <JobProgressPanel
                  jobId={jobIdsByDocument[String(doc.id)]}
                  onSettled={() => {
                    // Kick the listMatterDocuments poll immediately
                    // so the row's chip flips from 'uploading'
                    // (or 'processing') to 'indexed' / 'failed'
                    // without waiting for the next 5s tick.
                    fetchDocuments();
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
